import { defineStore } from "pinia";
import i18n from "@/i18n";
import router from "@/router";
import { getUserOs, OperatingSystem, isMobile } from "@/utils/misc";
import { getCurrentAppName } from "@/utils/biz/user";
import {
  getSessionStorage,
  setSessionStorage,
} from "@/utils/storage/sessionstorage";

type AnonymousUserToken = {
  user_id: string; // 游客 ID
  authorization: string; // 游客凭证 JWT
  anonymous: boolean; // 是否匿名（是游客），显然是 true
} | null;

type RegisteredUserToken = {
  user_id: string; // 用户 ID
  email: string; // 用户邮箱
  authorization: string; // 用户凭证
  anonymous: boolean; // 是否匿名（是游客），显然是 false
  is_first: number; // 是否新用户：0 否、1 是
  access_token: string; // 账号体系 token
} | null;

type UserInfo = {
  birthday: string;
  country: string;
  email: string;
  nickname: string;
  user_id: string;
  phone: string;
  create_time: string;
} | null;

const useUserStore = defineStore("userStore", {
  state() {
    return {
      ip: "", // 访客或用户的 IP 地址
      os: getUserOs(), // 访客或用户的操作系统
      locale: "en", // 访客或用户的当前语言（区域）
      // source 用户来源，请使用 getters 中的值
      isMobile: isMobile(), // 访客或用户终端是否为移动端
      anonymousUserToken: <AnonymousUserToken>null, // 匿名用户身份信息（访客）
      registeredUserToken: <RegisteredUserToken>null, // 登录用户身份信息
      userInfo: <UserInfo>null, // 用户信息
      showLogin: false, // 是否显示登录弹框，请在登录组件中监听此变量的变化
      loginPopupSource: "PromptLoginText", // 登录弹窗触发来源（用于埋点 label1）
      isVideoEnhancerVip: false, // 是否是 Video Enhancer 订阅用户
      isVideoGeneratorVip: false, // 是否是 Video Generator 订阅用户
      isVocalRemoverVip: false, // 是否是 Vocal Remover 订阅用户
      currentFunction: "", // 当前所在项目
      isLoginStatus: 0, // 0未登录，1微信登录已绑定手机号，2未绑定手机号
      loginRequestId: 0, // 微信登录的requestId
      coins: 0, // 用户总金币数
      vipExpireTimestamp: 0, // VIP权益过期时间（时间戳，秒）
    };
  },
  getters: {
    source(): string {
      let source = getSessionStorage("source");
      return source || "other";
    },
    abGroupName(): string {
      if (this.ip === "") return "";
      let lastDigit = Number(this.ip.split(".").pop());
      if (lastDigit % 2 === 1) {
        return "A";
      } else {
        return "B";
      }
    },
    isVip(): boolean {
      const name = router.currentRoute.value.name;

      if (name === "vocalRemoverIndex" || name === "vocalRemoverEditor") {
        return this.isVocalRemoverVip;
      }
      if (name === "videoEnhancerIndex" || name === "videoEnhancerEditor") {
        return this.isVideoEnhancerVip;
      }
      if (name === "videoGeneratorIndex" || name === "videoGeneratorEditor") {
        return this.isVideoGeneratorVip;
      }

      return false;
    },
  },
  actions: {
    setIp(ip: string) {
      this.ip = ip;
    },
    setOs(os: OperatingSystem) {
      this.os = os;
    },
    setLocale(locale: string) {
      this.locale = locale;
      i18n.global.locale.value = locale as any;
      // if (this.isRtlLang && !this.isMobile) {
      //   document.body.style.direction = "rtl";
      // } else {
      document.body.style.direction = "";
      // }
    },
    setSource(source: string | undefined) {
      if (source === undefined || source === "") return;

      let sourceInSession = getSessionStorage("source");
      if (sourceInSession === source) return;

      setSessionStorage("source", source);
    },
    setIsMobile(isMobile: boolean) {
      this.isMobile = isMobile;
    },
    setAnonymousUserToken(anonymousUserToken: AnonymousUserToken) {
      this.anonymousUserToken = anonymousUserToken;
    },
    setRegisteredUserToken(registeredUserToken: RegisteredUserToken) {
      this.registeredUserToken = registeredUserToken;
    },
    setUserInfo(userInfo: UserInfo) {
      this.userInfo = userInfo;
    },
    setShowLogin(showLogin: boolean) {
      this.showLogin = showLogin;
    },
    setLoginPopupSource(source: string) {
      this.loginPopupSource = source || "PromptLoginText";
    },
    setIsVideoEnhancerVip(isVip: boolean) {
      this.isVideoEnhancerVip = isVip;
    },
    setIsVideoGeneratorVip(isVip: boolean) {
      this.isVideoGeneratorVip = isVip;
    },
    setIsVocalRemoverVip(isVip: boolean) {
      this.isVocalRemoverVip = isVip;
    },
    setCurrentFunction(func: string) {
      this.currentFunction = func;
    },
    setIsLoginStatus(isLoginStatus: number) {
      this.isLoginStatus = isLoginStatus;
    },
    setLoginRequestId(loginRequestId: number) {
      this.loginRequestId = loginRequestId;
    },
    setCoins(coins: number) {
      this.coins = coins;
    },
    setVipExpireTimestamp(timestamp: number) {
      this.vipExpireTimestamp = timestamp;
    },
  },
  persist: true,
});

export default useUserStore;
